import { z } from "zod";

import { UUID } from "../generic/uuid.ts";

// ================================================
// ENUMS
// ================================================

// DesignMimeType
export const DesignMimeType = z.enum(["image/jpeg", "image/png"]);
export type DesignMimeType = z.infer<typeof DesignMimeType>;

// ================================================
// CONSTANTS
// ================================================

export const DESIGN_MAX_COUNT: number = 10;
// 3 MB - Anthropic's max image file size limit is 5 MB. However, image files are converted to
// base64 encoded strings, which take more space, so we set the limit to 3 MB.
// Note that the same limit is used on Supbase (see 20260313000001_create_chat_designs_storage_file_bucket.sql)
export const DESIGN_MAX_SIZE: number = 3 * 1024 * 1024;
export const DESIGN_SUPPORTED_MIME_TYPES: DesignMimeType[] =
  DesignMimeType.options;

// ================================================
// SELECT
// ================================================

// TODO : just take these types from DB schema

// Design
export const Design = z
  .strictObject({
    id: UUID,
    message_id: UUID,
    screen_id: UUID.nullable(),
    name: z.string().trim().min(1),
    size: z.int().min(1).max(DESIGN_MAX_SIZE),
    mime_type: DesignMimeType,
    path: z.string().trim().min(1),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .refine((data) => data.created_at <= data.updated_at, {
    message: '"created_at" must be before or equal to "updated_at"',
  });
export type Design = z.infer<typeof Design>;

export const DesignWithData = Design.safeExtend({
  data: z.base64(),
});
export type DesignWithData = z.infer<typeof DesignWithData>;

// ================================================
// INSERT
// ================================================

const omitInsertFields = {
  created_at: true,
  updated_at: true,
  screen_id: true,
} as const;

// DesignInsert
export const DesignInsert = Design.omit(omitInsertFields)
  .partial({ id: true })
  .extend({
    screen_id: z.string(),
  });
export type DesignInsert = z.infer<typeof DesignInsert>;
